import isPlainObject from 'lodash/isPlainObject';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';

export function buildPrefix(major, minor) {
  return `${major}/${minor}`;
}
const omitDeep = (o, test) =>
  Object.keys(o)
    .filter(k => !test(o[k]))
    .reduce(
      (result, k) => ({
        ...result,
        [k]: isPlainObject(o[k]) ? omitDeep(o[k], test) : o[k]
      }),
      {}
    );

export default class Base {
  constructor(defaults = {}, options = {}) {
    const prefix = options.prefix || this.constructor.name;
    this.defaults = defaults;
    this.PREFIX = buildPrefix(this.constructor.PREFIX, prefix);
    this.INITIALIZE = `${this.PREFIX}_${this.constructor.INITIALIZE}`;
    this.enhancer = this.enhancer.bind(this);
    this.middleware = this.middleware.bind(this);
    this.initialize = dispatch => {
      this.initialize = () => {};
      dispatch({ type: this.INITIALIZE });
    };
  }

  static PREFIX = '@@base';
  static INITIALIZE = 'INIT';
  static STATES = [];

  prefix() {
    return this.PREFIX;
  }

  isType(type) {
    return type.startsWith(`${this.PREFIX}/`);
  }

  isInit(type) {
    return type === this.INITIALIZE;
  }

  isState(type, state) {
    return this.isType(type) && type.endsWith(`_${state}`);
  }

  configure(defaults) {
    this.defaults = omitDeep({ ...(defaults || this.defaults) }, isNull);
    return { ...this.defaults };
  }

  update(defaults = {}) {
    return this.configure(merge({}, { ...this.defaults }, defaults));
  }

  buildStateTypes(type) {
    return this.constructor.STATES.map(state => `${type}_${state}`);
  }

  buildTypes(type) {
    return [
      `${this.PREFIX}/${type}`,
      ...this.buildStateTypes(`${this.PREFIX}/${type}`)
    ];
  }

  buildAction(action) {
    const { payload = {} } = action;
    const nextAction = omitDeep(
      {
        ...action,
        payload: {
          ...merge({}, { options: { ...this.defaults } }, payload)
        }
      },
      isUndefined
    );
    if (payload.options && payload.options.headers) {
      nextAction.payload.options.headers = payload.options.headers;
    }
    return nextAction;
  }
  // eslint-disable-next-line no-unused-vars
  before(action, states, next, store) {
    return Promise.resolve(action);
  }
  // eslint-disable-next-line no-unused-vars
  after(action, states, next, store) {
    return Promise.resolve(action);
  }
  // eslint-disable-next-line no-unused-vars
  main(action, states, next, store) {
    return Promise.resolve(action);
  }
  // eslint-disable-next-line no-unused-vars
  error(action, states, next, store) {
    return Promise.resolve(action);
  }

  getError(action = {}) {
    const { error } = action;
    return error;
  }

  hasError(action) {
    return !!this.getError(action);
  }

  read(state = {}) {
    return { ...state };
  }
  // eslint-disable-next-line no-unused-vars
  write(state = {}) {}
  // eslint-disable-next-line no-unused-vars
  end(action, states, next, store) {}

  enhancer(next) {
    return (reducer, initialState, enhancer) => {
      if (
        typeof initialState === 'function' &&
        typeof enhancer === 'undefined'
      ) {
        enhancer = initialState;
        initialState = undefined;
      }
      const store = next(reducer, this.read(initialState), enhancer);
      store.subscribe(() => {
        this.write(store.getState());
        this.initialize(store.dispatch);
      });
      return store;
    };
  }

  middleware(store) {
    return next => action => {
      this.initialize(next);

      if (!this.isType(action.type)) return next(action);

      let actionState = this.buildAction(action);
      const states = this.buildStateTypes(actionState.type);

      return this.before(actionState, states, next, store)
        .then(action => this.main((actionState = action), states, next, store))
        .then(action => this.after((actionState = action), states, next, store))
        .then(
          action => {
            this.end(action, states, next, store);
            return action;
          },
          error => this.error({ ...actionState, error }, states, next, store)
        );
    };
  }
}



// WEBPACK FOOTER //
// ./js/store/middleware/base.js